import {
  Button,
  mergeClasses,
  Slider,
} from '@fluentui/react-components';
import { Speaker216Filled, SpeakerMute16Filled } from '@fluentui/react-icons';
import React, { useRef, useState } from 'react';
import { useStyles } from './style';
import type { VolumeControlsProps } from '../types';

const DELTA_DIVISOR = 20; // 20 gives 5% delta
const MAX_VOLUME = 1;

export const VolumeControls = ({
  usePlayerControls,
  i18n,
}: VolumeControlsProps) => {
  const classes = useStyles();
  const { setVolume, volume } = usePlayerControls();

  const [showVolumeSlider, setShowVolumeSlider] = useState(false);

  const volumeSliderRef = useRef<HTMLInputElement>(null);
  const volumeButtonRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);

  const delta = MAX_VOLUME / DELTA_DIVISOR;

  const handleMuteClick = () => {
    const isMuted = volume === 0;
    const newVolume = isMuted ? 1 : 0;
    if (isMuted) {
      setVolume(newVolume);
    } else {
      setVolume(0);
    }
  };

  const handleVolumeButtonFocussed = () => {
    setShowVolumeSlider(true);
  };

  const handleOnBlur = (event: React.FocusEvent<HTMLElement>) => {
    if (event && event.relatedTarget === volumeSliderRef.current) {
      setShowVolumeSlider(true);
    } else {
      setShowVolumeSlider(false);
    }
  };

  const handleOnMouseEnter = () => {
    setShowVolumeSlider(true);
  };

  const handleOnMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
    if (event && event.relatedTarget === volumeSliderRef.current) {
      setShowVolumeSlider(true);
    } else {
      setShowVolumeSlider(false);
    }
  };

  const handleSliderOnMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
    if (event && event.relatedTarget === volumeButtonRef.current) {
      setShowVolumeSlider(true);
    } else {
      setShowVolumeSlider(false);
    }
  };

  const handleSliderOnBlur = (event: React.FocusEvent<HTMLElement>) => {
    if (event && event.relatedTarget === volumeButtonRef.current) {
      setShowVolumeSlider(true);
    } else {
      setShowVolumeSlider(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'ArrowLeft') {
      const newVolume = volume - delta;
      setVolume(Math.max(newVolume, 0));
    } else if (event.key === 'ArrowRight') {
      const newVolume = volume + delta;
      setVolume(Math.min(newVolume, MAX_VOLUME));
    }
  };

  return (
    <span className={mergeClasses(classes.volumeControls, 'clipchamp-player-volume-controls')}>
      <Button
        className={
                      showVolumeSlider
                          ? mergeClasses(classes.volumeButton, classes.volumeButtonFocused)
                          : mergeClasses('volume-btn', classes.volumeButton)
                  }
        icon={volume === 0 ? <SpeakerMute16Filled /> : <Speaker216Filled />}
        shape="rounded"
        onClick={handleMuteClick}
        onFocus={handleVolumeButtonFocussed}
        onBlur={handleOnBlur}
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
        ref={volumeButtonRef}
        aria-label={i18n && i18n.getString(_TL_('volume'))}
      />
      {showVolumeSlider && (
        <Slider
          ref={volumeSliderRef}
          className={classes.volumeSlider}
          thumb={{
                          className: classes.volumeSliderThumb,
                      }}
          rail={{
                          className: classes.volumeSliderRail,
                      }}
          value={volume}
          max={1}
          min={0}
          step={0.01}
          onMouseLeave={handleSliderOnMouseLeave}
          onBlur={handleSliderOnBlur}
          onChange={(_, sliderValue) => {
                          setVolume(sliderValue.value);
                      }}
          onKeyDown={handleKeyDown}
          size="small"
        />
          )}
    </span>
  );
};
